import React, { useState, useEffect, useContext } from 'react';
import { useAppStore } from '../../../../../GlobalStore/GlobalStore.ts';
import { OverlayWrapper } from '../../../../OverlayWrapper.tsx';
import SettingRow from '../Subcomponents/SettingRow/SettingRow.tsx';
import SaveButton from '../Subcomponents/SaveButton/SaveButton.tsx';
import { isIde } from '../../../../../Utils/misc/envNameUtils.ts';
import { UnsavedChangesContext, TabStateContext } from '../../Settings.tsx';
import { writeSettings } from '../../../../../Utils/db/users/userSettings/writeSettings.ts';

const ALL_TOOLS = [
    { id: 'webSearch', label: 'Web Search', settingsKey: 'enableSearchInternet' },
    { id: 'imageCreate', label: 'Image Generation', settingsKey: 'enableGenerateImages' },
    { id: 'documentCreate', label: 'Document Creation', settingsKey: 'enableCreateDocument' },
    { id: 'runTerminal', label: 'Run Terminal Command', settingsKey: 'enableRunTerminalCommand' },
    { id: 'reviewChanges', label: 'Review Changes', settingsKey: 'enableReviewChanges' },
];

const getAvailableTools = (environment: string) => {
    return ALL_TOOLS.filter(tool => {
        // Filter out PDF/DOCX creation for IDE environments
        if (tool.id === 'documentCreate' && isIde(environment)) {
            return false;
        }

        // Filter out terminal command for non-IDE environments
        if (tool.id === 'runTerminal') {
            return isIde(environment);
        }

        // Filter out review changes for non-IDE environments
        if (tool.id === 'reviewChanges') {
            return isIde(environment);
        }

        return true;
    });
};

const Tools: React.FC = () => {
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const userObj = useAppStore((state: any) => state.userObj);
    const { setTabHasUnsavedChanges } = useContext(UnsavedChangesContext);
    const { getTabState, setTabState } = useContext(TabStateContext);
    const { environment } = environmentObj;

    const availableTools = getAvailableTools(environment);

    // Get persistent tab state or initialize with current values
    const persistentState = getTabState('Tools');

    // Local state for tracking unsaved changes
    const [localSettings, setLocalSettings] = useState(() => {
        if (persistentState.localSettings) {
            return persistentState.localSettings;
        }
        const initial: any = {};
        availableTools.forEach(tool => {
            initial[tool.settingsKey] = settingsObj[tool.settingsKey] ?? true;
        });
        return initial;
    });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(persistentState.hasUnsavedChanges || false);

    // Update local settings when global settings change (only when not editing locally)
    useEffect(() => {
        if (!hasUnsavedChanges) {
            const newSettings: any = {};
            availableTools.forEach(tool => {
                newSettings[tool.settingsKey] = settingsObj[tool.settingsKey] ?? true;
            });
            setLocalSettings(newSettings);
        }
    }, [settingsObj.enableSearchInternet, settingsObj.enableGenerateImages, settingsObj.enableCreateDocument, settingsObj.enableRunTerminalCommand, settingsObj.enableReviewChanges, environment, hasUnsavedChanges, availableTools]);

    // Report unsaved changes status to parent Settings component
    useEffect(() => {
        setTabHasUnsavedChanges('Tools', hasUnsavedChanges);
    }, [hasUnsavedChanges, setTabHasUnsavedChanges]);

    // Persist state changes to TabStateContext
    useEffect(() => {
        setTabState('Tools', {
            localSettings,
            hasUnsavedChanges
        });
    }, [localSettings, hasUnsavedChanges, setTabState]);

    const handleToggle = (settingsKey: string) => {
        const newLocalSettings = {
            ...localSettings,
            [settingsKey]: !localSettings[settingsKey]
        };

        setLocalSettings(newLocalSettings);

        // Check if the new settings are different from original global settings
        const hasActualChanges = availableTools.some(tool => {
            const currentValue = newLocalSettings[tool.settingsKey] ?? true;
            const originalValue = settingsObj[tool.settingsKey] ?? true;
            return currentValue !== originalValue;
        });

        setHasUnsavedChanges(hasActualChanges);
    };

    const handleSave = async () => {
        try {
            // Clear unsaved changes flag immediately to prevent confirmation popup if user closes quickly
            setHasUnsavedChanges(false);

            // Update global state
            setSettingsObj({
                ...settingsObj,
                ...localSettings
            });

            // Save to persistent storage
            await writeSettings(userObj.user, localSettings, environmentObj.environment);
        } catch (error) {
            console.error('Failed to save tools settings:', error);
            // If save fails, restore the unsaved changes flag
            setHasUnsavedChanges(true);
        }
    };

    return (
        <OverlayWrapper type="paidOnly" customTooltipContent="Tools are paid only features">
            <div className="dark:text-neutral-300 space-y-4 flex flex-col h-full">
                <div className="flex-1 space-y-3">
                    {availableTools.map((tool) => (
                        <SettingRow
                            key={tool.id}
                            label={tool.label}
                            content={
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id={tool.id}
                                        checked={localSettings[tool.settingsKey] ?? true}
                                        onChange={() => handleToggle(tool.settingsKey)}
                                        className="h-4 w-4 accent-blue-600 cursor-pointer"
                                    />
                                </div>
                            }
                        />
                    ))}
                </div>

                <SaveButton
                    hasUnsavedChanges={hasUnsavedChanges}
                    onSave={handleSave}
                />
            </div>
        </OverlayWrapper>
    );
};

export default Tools;
